import { supabase } from '@/integrations/supabase/client';

export interface PurchasedKey {
  id: string;
  player_id: string;
  game_id: string;
  key_id: string | null;
  coins_spent: number;
  purchased_at: string;
  game: {
    id: string;
    title: string;
    cover_image_url: string | null;
    steam_url: string | null;
    steam_game_id: number | null;
  };
  key?: {
    id: string;
    key_code: string;
  };
}

export async function purchaseGameWithCoins(gameId: string, playerId: string, coinsCost: number): Promise<{ success: boolean; error?: string; keyCode?: string }> {
  try {
    // Start a transaction
    const { data: transactionData, error: transactionError } = await supabase.rpc('purchase_game_with_coins', {
      p_game_id: gameId,
      p_player_id: playerId,
      p_coins_cost: coinsCost
    });

    if (transactionError) {
      console.error('Transaction error:', transactionError);
      return { success: false, error: transactionError.message };
    }

    if (transactionData && transactionData.length > 0) {
      const result = transactionData[0];
      if (result.success) {
        return { success: true, keyCode: result.key_code };
      } else {
        return { success: false, error: result.message };
      }
    }

    return { success: false, error: 'No data returned from transaction' };
  } catch (error) {
    console.error('Error purchasing game with coins:', error);
    return { success: false, error: 'An error occurred while purchasing the game' };
  }
}

export async function getPlayerPurchasedKeys(playerId: string): Promise<PurchasedKey[]> {
  try {
    const { data, error } = await supabase
      .from('purchased_keys')
      .select(`
        id,
        player_id,
        game_id,
        key_id,
        coins_spent,
        purchased_at,
        game:games(
          id,
          title,
          cover_image_url,
          steam_url,
          steam_game_id
        ),
        key:keys(
          id,
          key_code
        )
      `)
      .eq('player_id', playerId)
      .order('purchased_at', { ascending: false });

    if (error) {
      console.error('Error fetching purchased keys:', error);
      return [];
    }

    // If key join didn't work, try to fetch keys separately
    if (data && data.length > 0) {
      const keysWithData = await Promise.all(
        data.map(async (purchase) => {
          if (purchase.key_id && !purchase.key) {
            const { data: keyData, error: keyError } = await supabase
              .from('keys')
              .select('id, key_code')
              .eq('id', purchase.key_id)
              .single();

            if (keyError) {
              console.error('Error fetching key:', keyError);
              return purchase;
            }

            return {
              ...purchase,
              key: keyData
            };
          }
          return purchase;
        })
      );

      return keysWithData;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching purchased keys:', error);
    return [];
  }
}

export async function hasPlayerPurchasedGame(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('purchased_keys')
      .select('id')
      .eq('game_id', gameId)
      .eq('player_id', playerId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error checking if game was purchased:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking if game was purchased:', error);
    return false;
  }
}
